/* istanbul ignore file */
'use client'

import clsx from 'clsx'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Agenda,
    Bookings,
    Comments,
    EventBottomBar,
    EventInformation,
    EventMasthead,
    Page,
    PageProps,
    Participants,
    Speakers,
} from '../../../../components/client'
import {
    useNotification,
    useRouter,
    useTranslation,
} from '../../../../lib/hooks'
import { selectEventLogoHref } from '../../../../lib/selectors'
import { EventQueryEvent, RedirectStatus } from '../../../../lib/types'
import { dayjsFrom } from '../../../../lib/utils'
import styles from './page.module.css'

interface Props extends Pick<PageProps, 'className' | 'eventCreation'> {
    event: EventQueryEvent
}

const EventClientPage: FC<Props> = props => {
    const { t } = useTranslation()
    const notification = useNotification()
    const router = useRouter()
    const redirectNotificationShown = useRef(false)
    const [bookingsOpen, setBookingsOpen] = useState(false)
    const [bottomBarClosed, setBottomBarClosed] = useState(true)
    const startDate = dayjsFrom(
        props.event.startDate,
        props.event.timezone.name
    )
    const endDate = dayjsFrom(props.event.endDate, props.event.timezone.name)

    useEffect(() => {
        const status = router.searchParams.get('status')

        if (redirectNotificationShown.current || !status) {
            return
        }

        redirectNotificationShown.current = true
        const params = new URLSearchParams(router.searchParams)
        params.delete('status')

        router.replaceSearchParams(params)

        notification.confirm(
            status === RedirectStatus.PaymentSuccessful
                ? t('event:paymentConfirmation')
                : t('event:bookingConfirmation'),
            { modal: true }
        )
    }, [notification, router, t])

    const openBookings = useCallback(() => setBookingsOpen(true), [])

    const closeBookings = useCallback(() => setBookingsOpen(false), [])

    const navigationLinks = useMemo(() => {
        // TODO: Why is it suddenly necessary to prepend whole path to anchor?
        const url = `/event/${props.event.key}`

        const links = [
            {
                href: `${url}#information`,
                title: t('event:information.title'),
            },
        ]

        if (props.event.agendaFeature && props.event.agenda?.length) {
            links.push({
                href: `${url}#agenda`,
                title: t('event:agenda'),
            })
        }

        if (props.event.speakerFeature) {
            links.push({
                href: `${url}#speakers`,
                title: t('event:speakers'),
            })
        }

        if (props.event.participantFeature) {
            links.push({
                href: `${url}#participants`,
                title: t('event:participants.title'),
            })
        }

        return links
    }, [
        props.event.agenda?.length,
        props.event.agendaFeature,
        props.event.key,
        props.event.participantFeature,
        props.event.speakerFeature,
        t,
    ])

    return (
        <>
            <Page
                availableLocales={props.event?.language.available}
                className={clsx(styles.root, props.className)}
                eventCreation={props.eventCreation}
                logoHref={selectEventLogoHref(props.event)}
                masthead={
                    <EventMasthead
                        event={props.event}
                        onEventActionsInViewChange={setBottomBarClosed}
                        openBookings={openBookings}
                    />
                }
                navigationLinks={navigationLinks}
                navigationNode={
                    props.event.commentFeature && (
                        <Comments
                            eventId={props.event.id}
                            viewable={Boolean(props.event.viewerCommentFeature)}
                        />
                    )
                }
            >
                <EventInformation
                    additionalFields={props.event.additionalFields}
                    capacity={props.event.capacity}
                    description={props.event.description}
                    endDate={endDate}
                    eventId={props.event.id}
                    lowestPrice={props.event.lowestPrice}
                    online={props.event.online}
                    operations={props.event.operations}
                    organizations={props.event.organizerOrganizations}
                    payment={props.event.payment}
                    startDate={startDate}
                    timezone={props.event.timezone}
                    users={props.event.organizerUsers}
                    venue={props.event.venue}
                    viewerParticipant={props.event.viewerParticipant}
                />
                {props.event.agendaFeature && !!props.event.agenda?.length && (
                    <Agenda
                        agenda={props.event.agenda}
                        eventKey={props.event.key}
                        timezone={props.event.timezone.name}
                    />
                )}
                {props.event.speakerFeature && (
                    <Speakers eventId={props.event.id} />
                )}
                {props.event.participantFeature && (
                    <Participants
                        eventId={props.event.id}
                        viewable={Boolean(props.event.viewerParticipantFeature)}
                    />
                )}
            </Page>
            <Bookings
                eventKey={props.event.key}
                onClose={closeBookings}
                open={bookingsOpen}
            />
            <EventBottomBar
                eventKey={props.event.key}
                open={!bottomBarClosed}
                openBookings={openBookings}
            />
        </>
    )
}

export default EventClientPage
